import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Button,
  Chip,
  Paper,
  useMediaQuery,
  useTheme,
  Grid,
  IconButton,
  Box,
  Divider,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

export default function OpsSalesHistoryPage() {
  const pageTitle = "Sales History";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [copiedRef, setCopiedRef] = useState(null);

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [sales, setSales] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      try {
        const result = await axios.get(
          `/agents/ops/entities/${ref}/sales.json`,
        );
        setShowLoading(false);
        if (result.data?.status === "success") {
          const salesData = result.data.data?.sales || result.data.data || [];
          setSales(salesData);
          setPageLoaded(true);
        } else {
          setSeverity("error");
          setErrMsg(
            result.data.data?.message || "Failed to fetch sales history",
          );
          setOpen(true);
        }
      } catch (error) {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
        );
        setOpen(true);
      }
    };

    fetchData();
  }, [ref, refreshPage]);

  // Helpers
  const formatKoboToNaira = (amountInKobo) => {
    const nairaAmount = (Number(amountInKobo) || 0) / 100;
    return `₦${nairaAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedRef(text);
      setTimeout(() => setCopiedRef(null), 2000);
    }
  };

  const getStatusChip = (status, paid, successful) => {
    if (
      paid ||
      successful ||
      status === "PAID" ||
      status === "SUCCESSFUL" ||
      status === "SUCCESS"
    ) {
      return (
        <Chip
          label={status}
          color="success"
          size="small"
          sx={{ color: "#fff", fontWeight: "bold", fontSize: "0.7rem" }}
        />
      );
    }
    if (status === "AWAITING-PAYMENT") {
      return (
        <Chip
          label="AWAITING PAYMENT"
          color="warning"
          variant="outlined"
          size="small"
          sx={{ fontWeight: "bold", fontSize: "0.65rem" }}
        />
      );
    }

    return (
      <Chip
        label={String(status || "PENDING").toUpperCase()}
        color="error"
        size="small"
        sx={{ fontWeight: "bold", fontSize: "0.7rem" }}
      />
    );
  };

  // DataTable Structure (Desktop View)
  const tableColumns = [
    { Header: "Type", accessor: "type" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Status", accessor: "status", align: "left" },
    { Header: "Date Created", accessor: "created" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  const tableRows = sales.map((sale) => ({
    type: sale.type,
    amount: (
      <MDTypography variant="button" fontWeight="bold" color="dark">
        {formatKoboToNaira(sale.amount)}
      </MDTypography>
    ),
    status: sale.status,
    // status: (
    //   <MDBox display="flex" justifyContent="center">
    //     {getStatusChip(sale.status, sale.paid, sale.successful)}
    //   </MDBox>
    // ),
    created: sale.created,
    action: (
      <MDBox display="flex" justifyContent="center" gap={1}>
        {sale.status === "AWAITING-PAYMENT" && (
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={() =>
              navigate(
                `/ops/workspaces/entities/sales/payment/${sale.reference}`,
              )
            }
          >
            <Icon sx={{ mr: 0.5 }}>payment</Icon> Status
          </MDButton>
        )}
        <MDButton
          variant="outlined"
          color="dark"
          size="small"
          onClick={() =>
            navigate(`/ops/workspaces/entities/sales/${sale.reference}`)
          }
        >
          <Icon sx={{ mr: 0.5 }}>visibility</Icon> View
        </MDButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox px={{ xs: 1, sm: 2, md: 3 }} py={1} mb={6}>
        <MDBox display="flex" alignItems="center" mb={1}>
          <Button
            color="dark"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/ops/workspaces/entities/${ref}`);
            }}
          >
            Back
          </Button>
        </MDBox>

        {pageLoaded && (
          <MDBox mt={1}>
            <Card sx={{ height: "100%", borderRadius: 3, overflow: "hidden" }}>
              <MDBox
                p={2.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                borderBottom="1px solid #f0f0f0"
              >
                <MDTypography variant="h6" fontWeight="bold">
                  Sales History
                </MDTypography>
                <Chip
                  label={`${sales.length} Record${
                    sales.length !== 1 ? "s" : ""
                  }`}
                  size="small"
                  variant="outlined"
                />
              </MDBox>

              {sales.length > 0 ? (
                isMobile ? (
                  /* Mobile-first Stacked Cards View */
                  <MDBox p={2}>
                    <Grid container spacing={2}>
                      {sales.map((sale) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          key={sale.reference || sale.id}
                        >
                          <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2.5,
                              backgroundColor: "#ffffff",
                              borderColor: "#e2e8f0",
                              transition: "box-shadow 0.2s",
                              "&:active": {
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            <MDBox mt={-1} align="center">
                              {getStatusChip(
                              sale.status,
                              sale.paid,
                              sale.successful,
                            )}
                            </MDBox>
                            <Divider />
                            <MDBox
                              mt={1.5}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              mb={1}
                            >
                              <MDBox pr={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  display="block"
                                  fontWeight="medium"
                                >
                                  {sale.type || "Sale Order"}
                                </MDTypography>
                                <MDTypography
                                  variant="h5"
                                  fontWeight="bold"
                                  color="dark"
                                  sx={{ my: 0.5 }}
                                >
                                  {formatKoboToNaira(sale.amount)}
                                </MDTypography>
                              </MDBox>
                            </MDBox>

                            {/* Reference Tag */}
                            {/* {sale.reference && (
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                bgcolor="grey.100"
                                px={1.2}
                                py={0.5}
                                borderRadius={1.5}
                                my={1}
                              >
                                <MDTypography
                                  variant="caption"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.75rem",
                                  }}
                                  color="text"
                                >
                                  {sale.reference}
                                </MDTypography>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    copyToClipboard(sale.reference)
                                  }
                                  color={
                                    copiedRef === sale.reference
                                      ? "success"
                                      : "default"
                                  }
                                  sx={{ p: 0.5 }}
                                >
                                  <Icon fontSize="small">
                                    {copiedRef === sale.reference
                                      ? "check"
                                      : "content_copy"}
                                  </Icon>
                                </IconButton>
                              </MDBox>
                            )} */}

                            {/* Footer / Actions Bar */}
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mt={1.5}
                              pt={1.5}
                              borderTop="1px solid #f1f5f9"
                            >
                              <MDTypography variant="caption" color="text">
                                {sale.created}
                              </MDTypography>
                            </MDBox>
                            <MDBox mt={2.5} display="flex" gap={1} width="100%">
                              {sale.status === "AWAITING-PAYMENT" && (
                                <MDButton
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  onClick={() =>
                                    navigate(
                                      `/ops/workspaces/entities/sales/payment/${sale.reference}`,
                                    )
                                  }
                                  sx={{
                                    flex: 1,
                                    px: 1.5,
                                    py: 0.5,
                                  }}
                                  startIcon={<Icon>payment</Icon>}
                                >
                                  Status
                                </MDButton>
                              )}
                              <MDButton
                                variant="contained"
                                color="info"
                                size="small"
                                onClick={() =>
                                  navigate(
                                    `/ops/workspaces/entities/sales/${sale.reference}`,
                                  )
                                }
                                sx={{
                                  flex: 1,
                                  px: 1.5,
                                  py: 0.5,
                                  color: "#fff",
                                }}
                                startIcon={<Icon>east</Icon>}
                              >
                                View
                              </MDButton>
                            </MDBox>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                ) : (
                  /* Desktop Table View */
                  <DataTable
                    table={{ columns: tableColumns, rows: tableRows }}
                    isSorted={false}
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20],
                    }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )
              ) : (
                <MDBox p={5} textAlign="center">
                  <Icon fontSize="large" color="disabled" sx={{ mb: 1 }}>
                    receipt_long
                  </Icon>
                  <MDTypography variant="body2" color="text">
                    No sales recorded for this entity yet.
                  </MDTypography>
                </MDBox>
              )}
            </Card>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
